import React, { useCallback, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Alert,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { formatDealOptionCurrency, formatDealOptionPercent } from '../dealOptions/dealOptionsFormatters';
import ManageRoomRateSurchargesAction from '../surcharges/ManageRoomRateSurchargesAction';
import { handleExpandRowKeyDown, tabTableHeadSx, toggleExpandedRow } from '../surcharges/surchargeTabLayout';

import EgsSectionHeading from './EgsSectionHeading';
import type { AppliedEgsRatePlanGroup, AppliedEgsRoomRate, AppliedEgsSlot } from './appliedEgsRows';
import { countAppliedEgsRoomRates, countAppliedEgsSlots } from './appliedEgsRows';
import { EGS_SLOT_LABEL, getEgsMarginPercent } from './egsUtils';

const APPLIED_EGS_COLUMN_COUNT = 5;

const FIXED_CELLS_AROUND_LABEL = 2;

interface Props {
  error: unknown;
  groups: Array<AppliedEgsRatePlanGroup>;
  isLoading: boolean;
}

const ROOM_RATE_START_SX = {
  '& .MuiTableCell-root': { borderTop: '2px solid', borderTopColor: 'divider' },
};

function RoomRateRows({ isFirst, roomRate }: { isFirst: boolean; roomRate: AppliedEgsRoomRate }) {
  return (
    <>
      {roomRate.slots.map((appliedSlot: AppliedEgsSlot, index) => (
        <TableRow key={`${appliedSlot.slot}-${index}`} sx={index === 0 && !isFirst ? ROOM_RATE_START_SX : undefined}>
          {index === 0 && (
            <>
              <TableCell rowSpan={roomRate.slots.length} />
              <TableCell rowSpan={roomRate.slots.length} sx={{ verticalAlign: 'top' }}>
                <Stack alignItems="center" direction="row" gap={0.5}>
                  <Typography fontWeight={600} variant="body2">
                    {roomRate.roomTypeName}
                  </Typography>
                  {/* This table is read-only, so the room rate editor is where a wrong amount gets fixed. */}
                  <ManageRoomRateSurchargesAction href={roomRate.href} />
                </Stack>
              </TableCell>
            </>
          )}
          <TableCell>
            <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
              <Typography variant="body2">{`Extra ${EGS_SLOT_LABEL[appliedSlot.slot]}`}</Typography>
              {appliedSlot.isConflicting && <Chip color="warning" label="Duplicated" size="small" variant="outlined" />}
              {/* Paid at booking is the norm, so only the exception is shown. */}
              {appliedSlot.isPayableAtProperty && (
                <Chip color="info" label="Paid at property" size="small" variant="outlined" />
              )}
            </Stack>
          </TableCell>
          <TableCell align="right">
            <Typography color="text.secondary" variant="body2">
              {formatDealOptionCurrency(appliedSlot.currency, appliedSlot.cost)}
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Typography variant="body2">{formatDealOptionCurrency(appliedSlot.currency, appliedSlot.sell)}</Typography>
            <Typography color="text.secondary" variant="caption">
              {`${formatDealOptionPercent(getEgsMarginPercent(appliedSlot.cost, appliedSlot.sell))} margin`}
            </Typography>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

function RatePlanSection({
  group,
  isExpanded,
  onToggleExpanded,
}: {
  group: AppliedEgsRatePlanGroup;
  isExpanded: boolean;
  onToggleExpanded: (ratePlanId: string) => void;
}) {
  const toggle = useCallback(() => onToggleExpanded(group.ratePlanId), [group.ratePlanId, onToggleExpanded]);
  const slotCount = group.roomRates.reduce((total, roomRate) => total + roomRate.slots.length, 0);

  return (
    <>
      <TableRow
        hover
        role="button"
        sx={{ cursor: 'pointer', '& .MuiTableCell-root': { bgcolor: 'grey.50' } }}
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(event) => handleExpandRowKeyDown(event, toggle)}
      >
        <TableCell>{isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</TableCell>
        <TableCell colSpan={APPLIED_EGS_COLUMN_COUNT - FIXED_CELLS_AROUND_LABEL}>
          <Typography fontWeight={700} variant="body2">
            {group.ratePlanName}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography color="text.secondary" variant="caption">
            {`${group.roomRates.length} room ${group.roomRates.length === 1 ? 'rate' : 'rates'}, ${slotCount} ${slotCount === 1 ? 'charge' : 'charges'}`}
          </Typography>
        </TableCell>
      </TableRow>
      {isExpanded &&
        group.roomRates.map((roomRate, index) => (
          <RoomRateRows key={roomRate.roomRateId} isFirst={index === 0} roomRate={roomRate} />
        ))}
    </>
  );
}

export default function AppliedEgsTable({ error, groups, isLoading }: Props) {
  // Holds the open plans, so every plan starts closed and a refetch cannot reopen one the CM had tidied.
  const [expandedRatePlanIds, setExpandedRatePlanIds] = useState<Set<string>>(() => new Set());

  const toggleRatePlan = useCallback(
    (ratePlanId: string) => setExpandedRatePlanIds((current) => toggleExpandedRow(current, ratePlanId)),
    [],
  );

  const slotCount = countAppliedEgsSlots(groups);
  const roomRateCount = countAppliedEgsRoomRates(groups);

  return (
    <>
      <EgsSectionHeading
        description={
          isLoading || error
            ? 'What this offer charges for an extra guest.'
            : `${slotCount} ${slotCount === 1 ? 'charge' : 'charges'} across ${roomRateCount} room ${roomRateCount === 1 ? 'rate' : 'rates'}, grouped by rate plan.`
        }
        title="Already Applied EGS"
      />

      {isLoading ? (
        <Stack alignItems="center" direction="row" justifyContent="center" spacing={1} sx={{ py: 3 }}>
          <CircularProgress size={16} />
          <Typography color="text.secondary" variant="body2">
            Loading the surcharges applied to this offer.
          </Typography>
        </Stack>
      ) : error ? (
        <Alert severity="error" sx={{ mx: 3 }}>
          Unable to load the surcharges applied to this offer.
        </Alert>
      ) : (
        <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 900, tableLayout: 'fixed' }}>
            {/* Pinned: on auto layout the room column absorbs the slack and the two money columns collide. */}
            <colgroup>
              <col style={{ width: '5%' }} />
              <col style={{ width: '37%' }} />
              <col style={{ width: '24%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '18%' }} />
            </colgroup>
            <TableHead>
              <TableRow sx={tabTableHeadSx}>
                <TableCell />
                <TableCell>Room</TableCell>
                <TableCell>Guest type</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell align="right">Sell</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={APPLIED_EGS_COLUMN_COUNT}>
                    <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                      No room rate on this offer charges for an extra guest yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                groups.map((group) => (
                  <RatePlanSection
                    key={group.ratePlanId}
                    group={group}
                    isExpanded={expandedRatePlanIds.has(group.ratePlanId)}
                    onToggleExpanded={toggleRatePlan}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
